import { For, Show, createMemo, createSignal, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Text } from '~ui/text';
import { Button } from '~ui/button';
import { Divider } from '~ui/divider';
import { Table, type Column } from '~ui/table';
import { NA, formatCurrency, formatNumber, formatPercent } from '~/lib/format';
import { coveredFunds, exposures, notOverlapping, overlapping, overlappingValue, uncoveredFunds, type Axis, type Exposure } from '~/stores/exposure-store';
import { fundSymbolFor } from '~/data/look-through';
import { Treemap } from '~/features/treemap';
import ExposureCards from '~/features/exposure-cards';

// Three tabs, one per axis. Overlap used to be a fourth — a filtered view of the
// company axis, given a tab of its own because it is the question the site
// promises an answer to.
//
// It is now the top half of the Company tab instead. A filter presented as a
// sibling of three axes said it was a fourth way of cutting the book, which it is
// not; and it hid the thing it was filtering, so "which companies do I own twice"
// and "out of what" were two clicks apart. Side by side they are one screen: the
// left table is the finding, the right is everything it was drawn from.

// Which of your funds reach this bucket, and how much of it each one is. Reads
// as "HMAX 25% · HDIV 4.1%" — the weight inside that fund, not the share of your
// book, because that is the number the fund itself publishes.
const Sources: Component<{ exposure: Exposure }> = (props) => (
    <div class='flex min-w-0 items-center gap-2 truncate'>
        <For each={props.exposure.slices}>
            {(slice, i) => (
                <span class='shrink-0'>
                    <Show when={i() > 0}>
                        <Text tone='muted'> · </Text>
                    </Show>
                    <Text weight='medium'>{slice.fund}</Text> <Text tone='muted'>{formatPercent(slice.weight, 1)}</Text>
                </span>
            )}
        </For>
    </div>
);

const TICKER: Column<Exposure> = { id: 'ticker', header: 'Ticker', size: 0.6, cell: (e) => <Text weight='medium'>{e().ticker ?? NA}</Text> };

const columnsFor = (axis: Axis): Column<Exposure>[] => [
    ...(axis === 'company' ? [TICKER] : []),
    { id: 'name', header: axis === 'sector' ? 'Sector' : axis === 'geography' ? 'Region' : 'Company', size: 1.6, cell: (e) => <Text class='truncate'>{e().name}</Text> },
    { id: 'funds', header: 'Held through', size: 2, cell: (e) => <Sources exposure={e()} /> },
    { id: 'count', header: 'Funds', align: 'right', size: 0.5, cell: (e) => <Text tone={e().slices.length > 1 ? 'red' : 'muted'}>{formatNumber(e().slices.length)}</Text> },
    { id: 'share', header: 'Of funds', align: 'right', size: 0.7, cell: (e) => formatPercent(e().share, 2) },
    { id: 'value', header: 'Exposure', align: 'right', size: 1, cell: (e) => <Text weight='medium'>{formatCurrency(e().value)}</Text> },
];

const Exposures: Component = () => {
    const [axis, setAxis] = createSignal<Axis>('company');

    const rows = createMemo(() => exposures(axis()));

    // "Nothing here" and "nothing could be resolved" are different answers, and
    // only one of them means the book is fine.
    const empty = createMemo(() => {
        if (coveredFunds(axis()).length === 0) {
            return axis() === 'company'
                ? 'No fund you hold publishes its holdings — nothing to resolve'
                : `No fund you hold publishes a ${axis() === 'sector' ? 'sector' : 'geographic'} breakdown`;
        }
        return 'Nothing to resolve';
    });


    /** The map's tiles, for the axes that draw one. */
    const tiles = createMemo(() =>
        rows().map((e) => {
            // "Has no ticker" is not the same question as "is not a position".
            // Harvest publishes its fund-of-fund holdings by name only, so
            // HDIF's ten constituents — 124% of the fund, HBF at 22.9% and HTA
            // at 22.8% among them — arrive untickered and would otherwise be
            // greyed out as though they were cash.
            const asFund = e.ticker == null ? fundSymbolFor({ symbol: null, name: e.name }) : null;
            const label = e.ticker ?? asFund ?? e.name;
            return {
                key: e.key,
                label,
                value: e.value,
                share: e.share,
                // Only where the tile is not already showing it.
                hint: label === e.name ? undefined : e.name,
                // Muted means cash, options, currency forwards — the lines that
                // are not holdings. Company axis only: on sector and geography
                // no row has a ticker and this would mute the entire map.
                muted: axis() === 'company' && e.ticker == null && asFund == null,
                // How the tile divides between the funds reaching it. Already
                // sorted biggest first by the store, and already in dollars, so
                // a tile split 75/25 is drawn 75/25. One rule per boundary, so a
                // tile with a single source draws none — which is what makes the
                // divided ones stand out at a glance.
                slices: e.slices.map((s) => s.value),
            };
        }),
    );

    /** One of the two stacked Company tables: a title band naming which list it
        is, over a table that owns its own scroll. Two untitled tables of the
        same columns is two sets of companies you have to work out. */
    const Cell: Component<{ title: string; note?: string; children: JSX.Element }> = (props) => (
        <div class='flex min-h-0 min-w-0 flex-1 flex-col bg-background'>
            <div class='flex h-row shrink-0 items-center justify-between gap-2 border-b px-2 whitespace-nowrap'>
                <Text weight='medium'>{props.title}</Text>
                <Text tone='muted' class='truncate'>
                    {props.note}
                </Text>
            </div>
            {props.children}
        </div>
    );

    const Tab: Component<{ id: Axis; label: string }> = (props) => (
        // Trailing `!` — Button's own hover:bg-muted is emitted later in the
        // sheet and would otherwise win. Same note as dividends.tsx.
        <Button size='fill' label={props.label} class={axis() === props.id ? 'bg-muted-foreground/20 border-b-3 border-foreground hover:bg-muted-foreground/20!' : ''} onClick={() => setAxis(props.id)} />
    );

    return (
        <Panel>
            {/* Properties of the whole book, which a table of rows cannot state.
                They follow the tab, except the overlap count — see the note in
                exposure-cards.tsx.

                Above the tabs, not below them. The cards are the page's headline
                and the tabs choose what is under them — a tab strip on top read
                as chrome for the whole pane, cards included, when the cards are
                the one thing on the screen it does not switch. */}
            <div class='shrink-0'>
                <ExposureCards axis={axis()} />
            </div>

            {/* No pane title. The lit nav row already says Exposure, and the
                tabs are the only thing here that says anything new. */}
            <Toolbar>
                <ToolbarSection>
                    <Tab id='company' label='Company' />
                    <Tab id='sector' label='Sector' />
                    <Tab id='geography' label='Geography' />
                </ToolbarSection>
                <ToolbarSection>
                    {/* Coverage differs per axis — the issuers publish different
                        things — so this is recomputed for the tab you are on
                        rather than stated once for the pane. */}
                    <Show when={uncoveredFunds(axis()).length > 0}>
                        <Text tone='red' class='whitespace-nowrap pr-2'>
                            Not published by: {uncoveredFunds(axis()).join(', ')}
                        </Text>
                    </Show>
                </ToolbarSection>
            </Toolbar>

            {/* One split for every axis: the exact reading on the left, the
                shape on the right. Area is hard to compare precisely — two rows
                within a few percent look alike — which is why the numbers stay
                on screen beside the map rather than a hover away.

                Company stacks two tables in that left half rather than one. The
                overlap list is the finding and the rest is the set it was drawn
                from, and stacked they are read down a single column of the same
                seven columns — side by side they would have been two tables you
                had to keep telling apart. */}
            <div class='flex min-h-0 flex-1 gap-px bg-border'>
                <div class='flex min-w-0 flex-1 flex-col gap-px bg-border'>
                    {/* The wrapper is bg-border so the gap-px between the two
                        stacked Company cells reads as a seam. Each Cell paints
                        its own background over it — a bare table has nothing to,
                        so it needs one of its own or the seam colour becomes the
                        table's background. */}
                    <Show
                        when={axis() === 'company'}
                        fallback={
                            <div class='flex min-h-0 flex-1 flex-col bg-background'>
                                <Table columns={columnsFor(axis())} rows={rows()} empty={empty()} />
                            </div>
                        }>
                        <Cell
                            title='Held more than once'
                            note={overlapping().length === 0 ? undefined : `${formatNumber(overlapping().length)} · ${formatCurrency(overlappingValue())}`}>
                            <Table
                                columns={columnsFor('company')}
                                rows={overlapping()}
                                empty={coveredFunds('company').length === 0 ? empty() : 'No company is reached through more than one of your funds'}
                            />
                        </Cell>
                        {/* Everything the list above was drawn from, less what it
                            caught. Not "held once": it also holds the lines with
                            no ticker to match on — cash, options, currency
                            forwards, and the fund-of-fund holdings one issuer
                            publishes by name only — none of which were eligible
                            for the overlap test at all. The two partition the
                            axis, so nothing is in both and nothing is in
                            neither. */}
                        <Cell title='Everything else' note={`${formatNumber(notOverlapping().length)} of ${formatNumber(rows().length)}`}>
                            <Table columns={columnsFor('company')} rows={notOverlapping()} empty={empty()} />
                        </Cell>
                    </Show>
                </div>

                <div class='flex min-w-0 flex-1 flex-col bg-background'>
                    <Treemap items={tiles()} empty={empty()} />
                </div>
            </div>
        </Panel>
    );
};

export default Exposures;
